<template>
    <div id="ProfileLongStory" :class="minified ? 'mb-4 space-y-6' : 'space-y-6 sm:space-y-10'">
        <template v-if="isAnonymous">
            <CHeading variant="3">Profesionálne skúsenosti</CHeading>

            <CCardBox>
                <JobsListView :is-anonymous="isAnonymous" :jobs="profile.jobs" />
            </CCardBox>
        </template>
        <template v-else>
            <div v-if="profile.hobby">
                <CHeading :variant="headingVariant">Viac o mne</CHeading>

                <CCardBox>
                    <span v-html="profile.hobby"></span>
                </CCardBox>
            </div>

            <div v-if="isInterestsBlockVisible">
                <CHeading :variant="headingVariant">Kľúčové slová o mne</CHeading>

                <CTags v-if="preview" :tags="profile.interests" variant="gray" />
                <CSearchTags v-else :tags="profile.interests" variant="gray" />
            </div>

            <div v-if="!isTalentBaseProfile && isShortProfileBlockVisible">
                <CHeading :variant="headingVariant">Krátky popis</CHeading>

                <CCardBox>
                    <span v-html="profile.shortProfile"></span>
                </CCardBox>
            </div>

            <template v-if="favoriteLocation">
                <CHeading :variant="headingVariant">Mesto alebo kraj, ku ktorému má ❤️ najbližšie</CHeading>
                <CCardBox>
                    {{ favoriteLocation }}
                </CCardBox>
            </template>

            <div v-if="isJobsBlockVisible">
                <CHeading :variant="headingVariant">Profesionálne skúsenosti</CHeading>

                <CCardBox>
                    <JobsListView :is-anonymous="isAnonymous" :jobs="profile.jobs" />
                </CCardBox>
            </div>

            <!-- TALENTBASE -->
            <div v-if="isShowTalentPreferences && profile.talentBaseUserProfile?.jobPreferences">
                <CHeading :variant="headingVariant">Pracovné preferencie</CHeading>

                <TalentBaseJobPreferences :job-preferences="profile.talentBaseUserProfile?.jobPreferences" />
            </div>

            <!-- TALENTBASE -->
            <div v-if="isShowTalentResponseForm && profile.talentBaseUserProfile?.moreAboutMe">
                <CHeading :variant="headingVariant">Odpovede na GROWNi otázky</CHeading>

                <TalentResponseForm class="mb-8" :more-about-talent="profile.talentBaseUserProfile?.moreAboutMe" />
            </div>

            <div v-if="isEductionBlockVisible">
                <CHeading :variant="headingVariant">Vzdelanie</CHeading>

                <div class="space-y-5">
                    <CCardBox v-if="isUniversitiesBlockVisible">
                        <EducationView headline="Vysokoškolské" :items="profile.universities" />
                    </CCardBox>

                    <CCardBox v-if="isHighSchoolsBlockVisible">
                        <EducationView headline="Stredoškolské" :items="profile.highSchools" />
                    </CCardBox>
                </div>
            </div>

            <div v-if="isLanguagesBlockVisible">
                <CHeading :variant="headingVariant">Jazykové znalosti</CHeading>

                <CCardBox>
                    <LanguageView :items="profile.languages" />
                </CCardBox>
            </div>

            <div v-if="isAchievementsBlockVisible">
                <CHeading :variant="headingVariant">Doplnkové aktivity a úspechy</CHeading>

                <CCardBox>
                    <AchievementView :items="profile.achievements" />
                </CCardBox>
            </div>

            <div v-if="isHobbiesAndInterestsVisible">
                <CHeading :variant="headingVariant">Hobby, záujmy</CHeading>

                <CCardBox>
                    <span v-html="profile.hobbiesAndInterests"></span>
                </CCardBox>
            </div>

            <CAlert v-if="isEmpty && !minified" class="my-2 text-left" icon="info" variant="info">
                Tvoj <strong>životopis</strong> na GROWNi zíva prázdnotou. Slúži ako tvoja vizitka, preto si daj
                záležať. <br />
                Ak si ho vyplníš, rapídne zvýšiš svoje šance na úspech.<br /><br />
                Kto ho uvidí? 👀
                <CList>
                    <li>potenciálny zamestnávateľ, keď sa uchádzaš o <strong>prácu</strong></li>
                    <li>mentori, keď ich požiadaš o <strong>mentoring</strong></li>
                    <li>organizácia, keď prejavíš záujem o jej <strong>dobrovoľnícky projekt</strong></li>
                    <li>ambasádor <strong>pracovnej skupiny</strong>, keď sa do nej prihlásiš</li>
                </CList>
                Životopis je zároveň súčasťou tvojho <strong>mentorského</strong> alebo
                <strong>dobrovoľníckeho profilu</strong>. Ak si ich <strong>zverejníš</strong>, potom to uvidia všetci
                ;-)
            </CAlert>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { UserProfile, UserProfileType } from '#shared/types/User'
import { EntityType } from '#shared/types/EntityType'
import TalentBaseJobPreferences from '@/components/talent-base/join/TalentBaseJobPreferences.vue'
import TalentResponseForm from '@/components/talent-base/join/TalentResponseForm.vue'
import CTags from '@/components/ui/CTags.vue'
import CSearchTags from '@/components/ui/CSearchTags.vue'

interface Props {
    profile: UserProfile
    profileViewType?: UserProfileType
    favoriteLocation?: string
    minified?: boolean
    preview?: boolean
    isAnonymous?: boolean
    isShowTalentPreferences?: boolean
    isShowTalentResponseForm?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    profileViewType: undefined,
    favoriteLocation: undefined,
    minified: false,
    preview: false,
    isAnonymous: false,
    isShowTalentPreferences: false,
    isShowTalentResponseForm: false
})

const store = useApplicationStore()

const isTalentBaseProfile = computed(() => {
    return props.profileViewType === EntityType.TalentBaseProfile
})

const isInterestsBlockVisible = computed(() => {
    return Boolean(props.profile.interests?.length)
})

const isShortProfileBlockVisible = computed(() => {
    return Boolean(props.profile.shortProfile?.length)
})

const isUniversitiesBlockVisible = computed(() => {
    return Boolean(props.profile.universities?.length)
})

const isHighSchoolsBlockVisible = computed(() => {
    return Boolean(props.profile.highSchools?.length)
})

const isEductionBlockVisible = computed(() => {
    return isUniversitiesBlockVisible.value || isHighSchoolsBlockVisible.value
})

const isJobsBlockVisible = computed(() => {
    return Boolean(props.profile.jobs?.length)
})

const isLanguagesBlockVisible = computed(() => {
    return Boolean(props.profile.languages?.length)
})

const isAchievementsBlockVisible = computed(() => {
    return Boolean(props.profile.achievements?.length)
})

const isHobbiesAndInterestsVisible = computed(() => {
    return Boolean(props.profile.hobbiesAndInterests?.length)
})

const isEmpty = computed(() => {
    return (
        store.userId === props.profile?.id &&
        !isInterestsBlockVisible.value &&
        !isEductionBlockVisible.value &&
        !isJobsBlockVisible.value &&
        !isAchievementsBlockVisible.value
    )
})

const headingVariant = computed(() => {
    return props.minified ? '4' : '3'
})
</script>